import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Trash2, RotateCcw, AlertTriangle, Loader2 } from "lucide-react";
import UserMenu from "../components/UserMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default function ProjectsTrash({ user, onLogout }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeConfirmName, setPurgeConfirmName] = useState("");
  const [purgeSubmitting, setPurgeSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projRes, cliRes] = await Promise.all([
        axios.get(`${API}/projects?only_deleted=true`, auth()),
        axios.get(`${API}/clients`, auth()),
      ]);
      setProjects(projRes.data || []);
      setClients(cliRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load trash");
    } finally {
      setLoading(false);
    }
  };

  const clientName = (cid) => clients.find((c) => c.id === cid)?.name || "—";

  const handleRestore = async (p) => {
    try {
      await axios.post(`${API}/projects/${p.id}/restore`, {}, auth());
      toast.success(`Restored '${p.name}'`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore");
    }
  };

  const openPurge = async (p) => {
    setPurgeTarget(p);
    setPurgePreview(null);
    setPurgeConfirmName("");
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${p.id}/delete-preview`, auth());
      setPurgePreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
      setPurgeTarget(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const submitPurge = async () => {
    if (!purgeTarget) return;
    if (purgeConfirmName.trim() !== purgeTarget.name.trim()) {
      toast.error("Project name does not match");
      return;
    }
    setPurgeSubmitting(true);
    try {
      const res = await axios.delete(
        `${API}/projects/${purgeTarget.id}/purge?confirm_name=${encodeURIComponent(purgeTarget.name)}`,
        auth()
      );
      toast.success(res.data?.message || "Purged");
      setPurgeTarget(null);
      setPurgeConfirmName("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to purge");
    } finally {
      setPurgeSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  return (
    <div data-testid="projects-trash-page">
      <Toaster position="top-right" richColors />
      <div className="erp-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-white hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Projects
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Trash2 className="w-6 h-6" /> Project Trash
              </h1>
              <p className="text-blue-100 text-sm mt-0.5">
                Restore or permanently purge soft-deleted projects (Super Admin)
              </p>
            </div>
          </div>
          <UserMenu user={user} onLogout={onLogout} variant="header" />
        </div>
      </div>

      <div className="erp-container">
        {projects.length === 0 ? (
          <div className="erp-card text-center py-12" data-testid="empty-state">
            <Trash2 className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-700 font-medium">Trash is empty</p>
          </div>
        ) : (
          <div className="erp-card">
            <h2 className="text-xl font-bold mb-4">{projects.length} project(s) in trash</h2>
            <table className="erp-table" data-testid="trash-table">
              <thead>
                <tr>
                  <th>Project ID</th>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Deleted</th>
                  <th>By</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} data-testid={`trash-row-${p.id}`}>
                    <td className="font-mono text-sm">{p.id}</td>
                    <td className="font-semibold">{p.name}</td>
                    <td className="text-gray-600">{clientName(p.client_id)}</td>
                    <td className="text-xs text-gray-500">
                      {p.deleted_at ? new Date(p.deleted_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
                    </td>
                    <td className="text-xs text-gray-500">{p.deleted_by || "—"}</td>
                    <td className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestore(p)}
                        data-testid={`restore-btn-${p.id}`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Restore
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openPurge(p)}
                        className="ml-2 bg-red-600 hover:bg-red-700"
                        data-testid={`purge-btn-${p.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Purge
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Purge Confirm Dialog */}
      <Dialog open={!!purgeTarget} onOpenChange={(o) => { if (!o) { setPurgeTarget(null); setPurgeConfirmName(""); } }}>
        <DialogContent className="max-w-2xl" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Permanently Delete Project
            </DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-12 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : purgePreview ? (
            <div className="space-y-4 mt-2 text-sm">
              <div className="bg-red-50 border border-red-200 rounded p-3 text-red-800">
                <span className="font-bold">This action is irreversible.</span> The following data
                will be deleted from the database and Cloudflare R2:
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {Object.entries(purgePreview.counts || {}).map(([k, v]) => (
                  <div key={k} className="border border-gray-200 rounded p-2 bg-gray-50">
                    <div className="text-2xl font-bold text-gray-800">{v}</div>
                    <div className="text-xs text-gray-500">{k.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-sm">Type the project name to confirm:</Label>
                <p className="font-mono text-base text-gray-800 my-1">{purgeTarget?.name}</p>
                <Input
                  value={purgeConfirmName}
                  onChange={(e) => setPurgeConfirmName(e.target.value)}
                  placeholder="Project name"
                  data-testid="purge-confirm-name-input"
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setPurgeTarget(null)}
              disabled={purgeSubmitting}
              data-testid="purge-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitPurge}
              disabled={purgeSubmitting || purgeConfirmName.trim() !== purgeTarget?.name?.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="purge-confirm-btn"
            >
              {purgeSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
